(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.SuttonCatalogue = api;
}(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const COLOURS = ['Black','White','Cream','Beige','Brown','Grey','Blue','Green','Red','Orange','Yellow','Pink','Purple','Gold','Silver','Natural','Multicolour'];
  const MATERIALS = ['Wool','Nylon','Polypropylene','Polyester','Sisal','Jute','Seagrass','Coir','Tencel','Recycled Plastic','Goat hair','Yak Hair','Cashmere','Cotton','Linen','Viscose','Acrylic','Silk'];
  const PATTERNS = ['Plain','Patterned','Loop','Herringbone','Stripe','Plaids','Tartan','Floral','Motif','Diamond','Axminster','Flatweave','Velvet'];
  const text = value => typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
  const canonical = value => text(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const label = value => text(value).replace(/[-_]+/g, ' ').replace(/\b[a-z]/g, c => c.toUpperCase());
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key) ? object[key] : undefined;

  function tagValue(tags, prefix) {
    const values = tags.map(tag => tag.match(new RegExp('^\\s*' + prefix + '\\s*:(.*)$', 'i'))).filter(Boolean).map(match => text(match[1]));
    const keys = [...new Set(values.map(canonical))];
    return { present: values.length > 0, valid: keys.length === 1 && !!keys[0], value: values[0] || '', key: keys[0] || '' };
  }
  function widths(product) {
    const index = product.optionNames.findIndex(name => /(^|\s)width(?:\s|$|\()/i.test(name));
    const values = product.variants.map(variant => text(variant.options[index < 0 ? 0 : index]));
    const numeric = value => /^(?:\d+(?:\.\d+)?|\.\d+)\s*(?:m|metres?|meters?)?$/i.test(value);
    // Older carpets use an unnamed numeric first option. Never extract a
    // number from arbitrary names such as "Blue 4" or "4 x 5".
    if (index < 0 && (!values.length || values.some(value => !numeric(value)))) return [];
    return [...new Set(values.filter(numeric).map(parseFloat).filter(value => value >= 0.3 && value <= 30))].sort((a, b) => a - b);
  }
  function normalise(products, registry) {
    const groups = registry.groups || {}, historical = registry.products || {}, aliases = registry.aliases || {};
    return products.filter(product => product.tags.includes('carpets') || product.tags.includes('calc-by-length')).map(product => {
      const old = own(historical, product.handle) || {};
      const rangeTag = tagValue(product.tags, 'range'), colourTag = tagValue(product.tags, 'colour');
      const historicalKey = text(product.legacyRangeKey) || text(old.key);
      const oldGroup = own(groups, historicalKey) || {};
      let rangeKey = '', brand = text(product.vendor) || text(oldGroup.brand), range = text(product.legacyRangeTitle) || text(oldGroup.range) || product.title;
      let groupingIssue = '';
      if (rangeTag.present) {
        if (!rangeTag.valid) groupingIssue = 'Conflicting or empty range tags';
        else if (!canonical(product.vendor)) groupingIssue = 'A supplier is required';
        else {
          const vendorKey = canonical(product.vendor);
          const knownKey = own(aliases, vendorKey + '|' + rangeTag.key);
          // Separate supplier and range boundaries to avoid cross-supplier
          // collisions; audited existing groups retain their original keys.
          rangeKey = knownKey || 'tag:' + vendorKey + ':' + rangeTag.key;
          const group = own(groups, rangeKey);
          range = group ? group.range : label(rangeTag.value);
          brand = group ? group.brand : text(product.vendor);
        }
      } else if (historicalKey) {
        rangeKey = historicalKey;
        brand = text(oldGroup.brand) || brand;
      }
      let colour = text(product.legacyColour) || text(old.colour) || product.title;
      if (colourTag.present) {
        if (!colourTag.valid) groupingIssue = 'Conflicting or empty colour tags';
        else if (canonical(colour) !== colourTag.key) colour = label(colourTag.value);
      }
      if (groupingIssue) rangeKey = '';
      if (old.noGroup) { rangeKey = ''; groupingIssue = 'Product identity requires review'; }
      const tags = new Set(product.tags.map(tag => text(tag).toLowerCase()));
      const colourGroups = COLOURS.filter(value => tags.has(value.toLowerCase()) || tags.has(value.toLowerCase() + ' riviera home'));
      const materials = MATERIALS.filter(value => tags.has(value.toLowerCase()));
      const patterns = PATTERNS.filter(value => tags.has(value.toLowerCase()) || (value === 'Tartan' && tags.has('plaid / tartan')));
      return {
        id: product.id, handle: product.handle, title: product.title, url: product.url,
        image: old.needsImageReview ? '' : product.image, available: product.available,
        vendor: product.vendor, brand, range, rangeKey, colour, groupingIssue,
        widths: widths(product), sku: product.variants.find(variant => text(variant.sku))?.sku || '',
        published: true, needsImageReview: !!old.needsImageReview,
        tags: product.tags.slice(), isRugEligible: product.tags.includes('calc-by-length'),
        material: materials.join(' · '), materials, colourGroups, patterns
      };
    }).sort((a, b) => a.brand.localeCompare(b.brand, 'en-GB') || a.range.localeCompare(b.range, 'en-GB') || a.colour.localeCompare(b.colour, 'en-GB') || a.handle.localeCompare(b.handle));
  }

  function parsePage(html) {
    if (typeof DOMParser === 'undefined') throw new Error('A catalogue HTML parser is required.');
    const document = new DOMParser().parseFromString(html, 'text/html');
    const scripts = document.querySelectorAll('script[data-sutton-catalogue]');
    if (scripts.length !== 1) throw new Error('The live carpet catalogue is unavailable. Please refresh to try again.');
    return JSON.parse(scripts[0].textContent);
  }
  function validProduct(product) {
    return product && typeof product === 'object' && (typeof product.id === 'string' && /^\d+$/.test(product.id) || Number.isSafeInteger(product.id) && product.id > 0) &&
      typeof product.handle === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(product.handle) &&
      typeof product.title === 'string' && !!product.title.trim() && typeof product.vendor === 'string' &&
      typeof product.url === 'string' && !!product.url && typeof product.image === 'string' && typeof product.available === 'boolean' &&
      Array.isArray(product.tags) && product.tags.every(tag => typeof tag === 'string') &&
      Array.isArray(product.optionNames) && product.optionNames.every(name => typeof name === 'string') &&
      Array.isArray(product.variants) && product.variants.every(variant => variant && Array.isArray(variant.options) && variant.options.every(value => typeof value === 'string') && (variant.sku == null || typeof variant.sku === 'string')) &&
      ['legacyRangeKey','legacyColour','legacyRangeTitle'].every(key => product[key] == null || typeof product[key] === 'string');
  }
  function validatePage(data, expectedPage, first) {
    if (!data || data.version !== 1 || !Number.isInteger(data.page) || data.page !== expectedPage ||
      !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100 || data.page > data.pages ||
      !Number.isInteger(data.total) || data.total < 0 || data.total > 25000 ||
      data.pages !== Math.max(1, Math.ceil(data.total / 250)) ||
      data.scanned !== Math.min(250, Math.max(0, data.total - (data.page - 1) * 250)) ||
      !Array.isArray(data.products) || data.products.length > data.scanned || !data.products.every(validProduct) ||
      (first && (data.pages !== first.pages || data.total !== first.total))) {
      throw new Error('The carpet catalogue changed or could not be fully loaded. Please refresh to try again.');
    }
    return data;
  }
  function validateRegistry(registry) {
    if (!registry || registry.version !== 1 || !registry.products || typeof registry.products !== 'object' || Array.isArray(registry.products) ||
      !registry.groups || typeof registry.groups !== 'object' || Array.isArray(registry.groups) ||
      !registry.aliases || typeof registry.aliases !== 'object' || Array.isArray(registry.aliases)) throw new Error('The carpet range information could not be loaded. Please refresh to try again.');
    return registry;
  }
  function createClient(options) {
    options = options || {};
    const request = options.fetch || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    const parser = options.parsePage || parsePage, now = options.now || Date.now;
    const cacheMs = options.cacheMs == null ? 60000 : options.cacheMs;
    const requestTimeoutMs = options.requestTimeoutMs == null ? 15000 : options.requestTimeoutMs;
    const cache = new Map();
    async function retrieve(url, legacyUrl) {
      if (!request) throw new Error('The carpet catalogue cannot be loaded in this browser.');
      async function read(endpoint, init, parse, message) {
        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        let timer;
        const timeout = new Promise((_, reject) => {
          timer = setTimeout(() => {
            reject(new Error('The carpet catalogue took too long to load. Please refresh to try again.'));
            if (controller) controller.abort();
          }, requestTimeoutMs);
        });
        try {
          return await Promise.race([timeout, (async () => {
            const response = await request(endpoint, { ...init, ...(controller ? { signal: controller.signal } : {}) });
            if (!response.ok) throw new Error(message);
            return await parse(response);
          })()]);
        } finally { clearTimeout(timer); }
      }
      // Prevent cached pages from different catalogue versions sharing pagination.
      const requestVersion = String(now());
      async function page(number, first) {
        const endpoint = new URL(url);
        endpoint.searchParams.set('page', String(number));
        endpoint.searchParams.set('_sutton', requestVersion);
        const data = await read(endpoint.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } }, async response => parser(await response.text()), 'The live carpet catalogue could not be loaded. Please refresh to try again.');
        return validatePage(data, number, first);
      }
      const registryPromise = (async () => {
        const data = await read(legacyUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } }, response => response.json(), 'The carpet range information could not be loaded. Please refresh to try again.');
        return validateRegistry(data);
      })();
      const [first, registry] = await Promise.all([page(1), registryPromise]);
      const pages = [first]; let next = 2;
      async function worker() {
        while (next <= first.pages) {
          const current = next++;
          pages[current - 1] = await page(current, first);
        }
      }
      await Promise.all(Array.from({ length: Math.min(3, first.pages - 1) }, worker));
      if (pages.length !== first.pages || pages.some((entry, index) => !entry || entry.page !== index + 1)) throw new Error('The live carpet catalogue is incomplete. Please refresh to try again.');
      const products = pages.flatMap(entry => entry.products), ids = new Set(), handles = new Set();
      for (const product of products) {
        if (ids.has(String(product.id)) || handles.has(product.handle)) throw new Error('The carpet catalogue changed while loading. Please refresh to try again.');
        ids.add(String(product.id)); handles.add(product.handle);
      }
      if (products.length > first.total) throw new Error('The live carpet catalogue is inconsistent. Please refresh to try again.');
      return normalise(products, registry);
    }
    function load(settings) {
      try {
        const base = typeof location !== 'undefined' ? location.href : undefined;
        const url = new URL(settings.url, base); url.searchParams.delete('page'); url.hash = '';
        const legacyUrl = new URL(settings.legacyUrl, base).href;
        const key = url.href, previous = cache.get(key);
        if (previous && previous.legacyUrl === legacyUrl && (previous.pending || now() - previous.created < cacheMs)) return previous.promise;
        const entry = { created: now(), legacyUrl, pending: true };
        entry.promise = retrieve(key, legacyUrl).then(products => { entry.pending = false; entry.created = now(); return products; }, error => { if (cache.get(key) === entry) cache.delete(key); throw error; });
        cache.set(key, entry);
        return entry.promise;
      } catch (error) { return Promise.reject(error); }
    }
    return { load };
  }
  const client = createClient();
  return { load: client.load, createClient, normalise };
}));
